import type { ProductCategory } from '@/data/products';

export type ProductLine = 'bondex' | 'bariyerli' | 'cift';

export type AkustikProfile = 'base' | 'piramit' | 'labirent' | 'yumurta' | 'duz';

export const DENSITY_OPTIONS = [120, 150, 180] as const;
export type DensityDns = (typeof DENSITY_OPTIONS)[number];

export const PRODUCT_LINE_THICKNESSES: Record<ProductLine, number[]> = {
  bondex: [10, 20, 30, 40, 50],
  bariyerli: [13, 23, 33, 43, 53],
  cift: [16, 26, 36, 46, 56],
};

const AKUSTIK_PROFILE_BY_SLUG: Record<string, AkustikProfile> = {
  'akustik-sunger': 'base',
  'akustik-piramit-sunger': 'piramit',
  'akustik-labirent-sunger': 'labirent',
  'akustik-yumurta-sunger': 'yumurta',
  'akustik-duz-sunger': 'duz',
};

function thicknessRange(min: number, max: number, step = 10): number[] {
  const options: number[] = [];
  for (let mm = min; mm <= max; mm += step) {
    options.push(mm);
  }
  return options;
}

export const AKUSTIK_THICKNESS_OPTIONS: Record<AkustikProfile, number[]> = {
  base: thicknessRange(10, 70),
  piramit: thicknessRange(40, 70),
  labirent: thicknessRange(30, 60),
  yumurta: thicknessRange(30, 60),
  duz: thicknessRange(10, 60),
};

const AKUSTIK_MIN_THICKNESS: Record<AkustikProfile, number> = {
  base: 10,
  piramit: 40,
  labirent: 30,
  yumurta: 30,
  duz: 10,
};

/** Minimum kalınlıktaki taban fiyat (₺/m²) */
const AKUSTIK_BASE_PRICE: Record<AkustikProfile, number> = {
  base: 500,
  piramit: 320,
  labirent: 320,
  yumurta: 320,
  duz: 320,
};

/** Her 10 mm artışta eklenen tutar (₺/m²) */
const AKUSTIK_STEP_PRICE: Record<AkustikProfile, number> = {
  base: 45,
  piramit: 48,
  labirent: 38,
  yumurta: 40,
  duz: 35,
};

export function getAkustikProfile(slug: string): AkustikProfile | null {
  return AKUSTIK_PROFILE_BY_SLUG[slug] ?? null;
}

export function isAkustikThicknessSelectable(category: ProductCategory): boolean {
  return category === 'Akustik Sünger';
}

export function getAkustikThicknessOptions(slug: string): number[] {
  const profile = getAkustikProfile(slug);
  return profile ? AKUSTIK_THICKNESS_OPTIONS[profile] : [];
}

export function getDefaultAkustikThickness(slug: string): number {
  const options = getAkustikThicknessOptions(slug);
  return options[0] ?? 30;
}

export function getAkustikPrice(slug: string, thicknessMm: number): number | undefined {
  const profile = getAkustikProfile(slug);
  if (!profile) return undefined;
  const options = AKUSTIK_THICKNESS_OPTIONS[profile];
  if (!options.includes(thicknessMm)) return undefined;
  const steps = (thicknessMm - AKUSTIK_MIN_THICKNESS[profile]) / 10;
  return AKUSTIK_BASE_PRICE[profile] + steps * AKUSTIK_STEP_PRICE[profile];
}

export function getAkustikThicknessRangeLabel(slug: string): string {
  const options = getAkustikThicknessOptions(slug);
  if (!options.length) return '10-70 mm';
  return `${options[0]}-${options[options.length - 1]} mm`;
}

/** Satış fiyatları (₺/m²) – yoğunluk ve kalınlığa göre */
export const SALES_PRICES: Record<ProductLine, Record<number, Record<DensityDns, number>>> = {
  bondex: {
    10: { 120: 110, 150: 135, 180: 160 },
    20: { 120: 200, 150: 240, 180: 280 },
    30: { 120: 290, 150: 365, 180: 430 },
    40: { 120: 400, 150: 505, 180: 610 },
    50: { 120: 475, 150: 675, 180: 765 },
  },
  bariyerli: {
    13: { 120: 350, 150: 400, 180: 500 },
    23: { 120: 400, 150: 500, 180: 570 },
    33: { 120: 450, 150: 570, 180: 720 },
    43: { 120: 500, 150: 635, 180: 820 },
    53: { 120: 550, 150: 700, 180: 900 },
  },
  cift: {
    16: { 120: 425, 150: 525, 180: 690 },
    26: { 120: 600, 150: 850, 180: 1050 },
    36: { 120: 650, 150: 800, 180: 1000 },
    46: { 120: 690, 150: 880, 180: 1120 },
    56: { 120: 800, 150: 1020, 180: 1300 },
  },
};

/** dB ses yalıtım değerleri – kalınlığa göre (tek değer veya aralık metni) */
export const SOUND_INSULATION_DB: Record<ProductLine, Record<number, string | number>> = {
  bondex: { 10: 10, 20: 12, 30: 14, 40: 16, 50: 18 },
  // Tek bariyerli Bondex – ortalama genel ↓ (dB) yaklaşma aralıkları
  bariyerli: {
    13: '≈ 25–30',
    23: '≈ 30–35',
    33: '≈ 35–40',
    43: '≈ 40–45',
    53: '≈ 45–50',
  },
  // Çift bariyerli Bondex – ortalama genel ↓ (dB) yaklaşma aralıkları
  cift: {
    16: '≈ 45–50',
    26: '≈ 50–55',
    36: '≈ 60–65',
    46: '≈ 70–80',
    56: '≈ 75–85',
  },
};

/** NRC değerleri – kalınlığa göre */
export const SOUND_NRC: Partial<Record<ProductLine, Record<number, string>>> = {
  bondex: {
    10: '0.40–0.55',
    20: '0.50–0.65',
    30: '0.60–0.75',
    40: '0.65–0.80',
    50: '0.70–0.85',
  },
  bariyerli: {
    13: '0.60–0.75',
    23: '0.70–0.85',
    33: '0.80–0.95',
    43: '0.85–0.98',
    53: '0.90–0.98',
  },
  cift: {
    16: '0.75–0.90',
    26: '0.80–0.95',
    36: '0.85–0.98',
    46: '0.90–0.98',
    56: '0.92–0.98',
  },
};

const CATEGORY_TO_LINE: Partial<Record<ProductCategory, ProductLine>> = {
  'Bondex Sünger': 'bondex',
  'Bariyerli Bondex': 'bariyerli',
  'Çift Bariyerli Bondex': 'cift',
};

export function getProductLine(category: ProductCategory): ProductLine | null {
  return CATEGORY_TO_LINE[category] ?? null;
}

export function hasVariantPricing(category: ProductCategory): boolean {
  return category in CATEGORY_TO_LINE;
}

export function getSalesPrice(line: ProductLine, thicknessMm: number, density: DensityDns): number | undefined {
  return SALES_PRICES[line][thicknessMm]?.[density];
}

export function getDefaultThickness(line: ProductLine): number {
  return PRODUCT_LINE_THICKNESSES[line][0];
}

export function getSoundInsulationLabel(line: ProductLine, thicknessMm: number): string | undefined {
  const value = SOUND_INSULATION_DB[line][thicknessMm];
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value} dB` : `${value} dB`;
}

export function getSoundNrcLabel(line: ProductLine, thicknessMm: number): string | undefined {
  return SOUND_NRC[line]?.[thicknessMm];
}

/** Buton altı ve teknik tabda öne çıkan özellik anahtarları */
export const HIGHLIGHT_SPEC_KEYS = ['Garanti', 'Teslimat', 'Ses Yalıtımı', 'NRC', 'Stok', 'Uygulama', 'Ölçü'] as const;

export function buildTopSpecs(thicknessMm: number | undefined, category: ProductCategory, productSlug?: string) {
  if (category === 'Akustik Sünger') {
    const kalinlik = thicknessMm
      ? `${thicknessMm} mm`
      : productSlug
        ? getAkustikThicknessRangeLabel(productSlug)
        : '10-70 mm';
    return [
      { k: 'Stok', v: 'Sınırsız' },
      { k: 'Teslimat', v: '1 gün' },
      { k: 'Garanti', v: 'Kurumsal' },
      { k: 'Ses Emilimi', v: 'NRC 0.85' },
      { k: 'Ölçü', v: '100 x 100 cm' },
      { k: 'Kalınlık', v: kalinlik },
      { k: 'Uygulama', v: 'Yapıştırma' },
      { k: 'Eklenebilir Özellik', v: 'Kendinden Yapışkanlı' },
      { k: 'Kurulum', v: 'Ücretli' },
      { k: 'Yüzey', v: 'Piramit / Yumurta / Labirent / Düz' },
      { k: 'Ses ve Yanmazlık Kodu', v: 'TS EN 13501-1 2019-12' },
      { k: 'Yanmazlık Sınıfı', v: 'TSE E Sınıfı' },
    ];
  }

  const line = getProductLine(category);
  const soundInsulation =
    line && thicknessMm ? getSoundInsulationLabel(line, thicknessMm) ?? '—' : '—';
  const nrcValue = line && thicknessMm ? getSoundNrcLabel(line, thicknessMm) : undefined;
  const kalinlik = thicknessMm ? `${thicknessMm} mm` : '—';

  return [
    { k: 'Garanti', v: 'Kurumsal' },
    { k: 'Teslimat', v: '1 gün' },
    { k: 'Ses Yalıtımı', v: soundInsulation },
    ...(nrcValue ? [{ k: 'NRC', v: nrcValue }] : []),
    { k: 'Stok', v: 'Sınırsız' },
    { k: 'Uygulama', v: 'Yapıştırma' },
    { k: 'Ölçü', v: '100 x 100 cm' },
    { k: 'Kalınlık', v: kalinlik },
    { k: 'Eklenebilir Özellik', v: 'Kendinden Yapışkanlı' },
    { k: 'Kurulum', v: 'Ücretli' },
    { k: 'Sertlik Sınıfı', v: 'ISO 868' },
    { k: 'Ses ve Yanmazlık Kodu', v: 'TS EN 13501-1 2019-12' },
    { k: 'TSE Sınıfı Ses Raporu', v: 'TSE EN ISO 10534-2' },
    { k: 'Yanmazlık Sınıfı', v: 'TSE E Sınıfı' },
  ];
}

export function getHighlightSpecs(specs: { k: string; v: string }[]) {
  const order = HIGHLIGHT_SPEC_KEYS as readonly string[];
  return order
    .map((key) => specs.find((s) => s.k === key))
    .filter((s): s is { k: string; v: string } => Boolean(s));
}
